import { GraphCanvasEngine } from '@/components/UI/GraphCanvas/GraphCanvasEngine';
import { wait } from '@/helpers';
import { _isNil, _notNil } from '@/littledash';
import type { AgentSessionApiId, AgentTreeV1 } from '@/model/Agent.model';
import { TerminalEventStatus, TERMINAL_SESSION_STATUSES } from '@/model/Agent.model';
import InVivoError from '@/model/InVivoError';
import { ApiService, ResponseType } from '@/support/ApiService';
import { AgentSessionStreamService } from '@/support/agentStream/AgentSessionStreamService';
import { ExceptionHandler } from '@/utils/ExceptionHandler';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ACTIVITY_EVENT_TYPES, isTerminalStatus } from './Activity/ActivityRow.utils';
import { AgentActivityEngine } from './Activity/AgentActivityEngine';
import {
  agentTreeGraphConfig,
  mapTreeNodeCreated,
  mapTreeToSnapshot,
  type AgentNodeHandlers,
  type AgentSessionMeta,
  type AgentTreeNodeData,
} from './AgentTree.utils';

/** Whether we opened a still-running session (live SSE) or a finished one (replay /events history). */
type SourceMode = 'beganLive' | 'loadedTerminal';

/**
 * Bounded retries for a `/tree` reconcile that fails while the session has NOT yet settled. Only this
 * window matters: after a terminal stream event the stream can close, so a failing settle-fetch has no
 * later trigger to recover it and would strand the root badge spinning. A small fixed budget with a
 * short backoff covers a transient blip without hammering the endpoint; once terminal is reached (or the
 * budget is spent) it reverts to best-effort.
 */
const MAX_TERMINAL_RECONCILE_RETRIES = 3;
const TERMINAL_RECONCILE_RETRY_MS = 1000;

/** Activity-history load state — only meaningful in the loadedTerminal path. */
export type HistoryState = 'idle' | 'loading' | 'error';

export interface AgentSessionStream {
  engine: GraphCanvasEngine<AgentTreeNodeData, AgentSessionMeta>;
  activityEngine: AgentActivityEngine;
  historyState: HistoryState;
  /**
   * Re-fetch `/tree` and re-hydrate the engine + activity status from the authoritative snapshot.
   * Exposed so an out-of-band action that changes the session server-side — confirming SEND domains
   * queues the validate execution, flipping the session back to `running` — can refresh the graph
   * immediately (re-spinning the root badge) rather than waiting for the next stream/drop trigger.
   */
  reconcile: () => Promise<void>;
}

/**
 * Owns the live machinery for one agent session and wires it together: the tree engine, the activity
 * engine, and (for a still-running session) the SSE transport. All instances are created once via lazy
 * `useState` initializers, so this hook must be called from a component that is mounted once and never
 * conditionally unmounted — the wiring effect is what feeds the engines, and it must survive the
 * `running → terminal` transition.
 *
 * Two mutually-exclusive data sources, selected once from the initial tree's status:
 *  - `beganLive`      — subscribe the transport to the engines + reconciliation, then `connect()`.
 *  - `loadedTerminal` — no stream; replay durable `/events` history into the activity engine once.
 *
 * Returns the engines (for the view to render) plus the activity-history load state.
 */
export const useAgentSessionStream = (
  sessionApiId: AgentSessionApiId,
  initialTree: AgentTreeV1,
  handlers: AgentNodeHandlers
): AgentSessionStream => {
  const [engine] = useState(() => {
    const e = new GraphCanvasEngine<AgentTreeNodeData, AgentSessionMeta>(agentTreeGraphConfig);
    e.setGraph(mapTreeToSnapshot(initialTree, handlers));
    return e;
  });
  const [sourceMode] = useState<SourceMode>(() =>
    TERMINAL_SESSION_STATUSES.has(initialTree.session.status) ? 'loadedTerminal' : 'beganLive'
  );
  const [service] = useState<AgentSessionStreamService | null>(() =>
    sourceMode === 'beganLive' ? new AgentSessionStreamService(sessionApiId) : null
  );
  const [activityEngine] = useState(() => {
    const engine = new AgentActivityEngine();
    engine.setStatus(initialTree.session.status);
    return engine;
  });
  const [historyState, setHistoryState] = useState<HistoryState>(sourceMode === 'loadedTerminal' ? 'loading' : 'idle');

  // Reconcile against /tree (authoritative). Fires on a terminal stream event or when the socket
  // drops (status 'reconnecting') — the run may have finished during the gap, so re-fetch the
  // authoritative graph + status and re-hydrate the engine. The transport owns reconnection (managed
  // backoff), so this never has to re-open the stream itself.
  //
  // /tree's session.status is the ONE authority for whether the whole session is terminal — a single
  // Redis stream is shared across every execution, so a terminal *stream event* marks the end of one
  // execution (e.g. the SEND validator's detect execution), not the session. So terminal settling
  // (resolve open activity rows, settle badges, close the stream) is driven from the reconciled status
  // HERE, never from the raw stream event — otherwise the detect execution's `completed`, replayed
  // from 0-0 on connect, would tear everything down before the validate execution streams.
  //
  // Single-flight, but a trigger that arrives mid-flight sets `pendingReconcileRef` so a fresh fetch
  // runs after the current one settles — a terminal event must never coalesce onto a stale in-flight
  // snapshot and miss the final terminal status. Terminal state is monotonic (see AgentActivityEngine),
  // so a later reconcile that fetched a stale pre-terminal `running` snapshot must not downgrade the
  // graph badge back to running — skip re-hydrating in that case.
  const reachedTerminalRef = useRef(false);
  const inFlightReconcileRef = useRef<Promise<void> | null>(null);
  const pendingReconcileRef = useRef(false);
  const reconcile = useCallback((): Promise<void> => {
    if (_notNil(inFlightReconcileRef.current)) {
      pendingReconcileRef.current = true; // a trigger arrived mid-flight — loop once more after this fetch
      return inFlightReconcileRef.current;
    }
    // One in-flight fetch at a time; a trigger that arrives mid-flight sets `pending` and this loop
    // runs a fresh fetch when the current one settles, so a terminal event never coalesces onto a stale
    // snapshot and misses the final terminal status.
    const run = (async (): Promise<void> => {
      // A failed /tree fetch normally just leaves the last-known graph in place — the stream will
      // trigger another reconcile. But while the session hasn't settled yet the failing fetch may be
      // the one meant to settle it (a terminal stream event fired, and the stream can then close), so
      // dropping it would strand the root badge spinning. Retry a bounded number of times in that case.
      let terminalRetriesRemaining = MAX_TERMINAL_RECONCILE_RETRIES;
      do {
        pendingReconcileRef.current = false;
        const response = await ApiService.call({
          endpoint: 'GET /api/v1/ai-agents/sessions/{sessionApiId}/tree',
          path: { sessionApiId },
          options: { onError: { throw: false, toast: false, capture: true, slug: 'agent-session-tree-reconcile' } },
        });
        if (response.type !== ResponseType.Success) {
          if (!reachedTerminalRef.current && terminalRetriesRemaining > 0) {
            terminalRetriesRemaining -= 1;
            await wait(TERMINAL_RECONCILE_RETRY_MS);
            pendingReconcileRef.current = true; // re-fetch: the session may have settled server-side
          }
          continue; // best-effort re-sync; a failed reconcile leaves the last-known graph in place
        }
        if (reachedTerminalRef.current && !isTerminalStatus(response.body.session.status)) {
          continue; // stale snapshot fetched before the run finished — don't regress terminal state
        }
        // `setGraph` replaces the whole node set. The tree is append-only (no node-deleted event), so a
        // node we hold that this /tree response lacks is one the live stream added AFTER the server took
        // this snapshot — preserve it, or a reconcile racing an in-flight `tree_node_created` would drop
        // it. `setGraph` still refreshes badges/layout for the authoritative nodes.
        const snapshot = mapTreeToSnapshot(response.body, handlers);
        const authoritativeIds = new Set(snapshot.nodes.map((node) => node.id));
        const streamedOnly = engine.getNodes().filter((node) => !authoritativeIds.has(node.id));
        engine.setGraph({ ...snapshot, nodes: [...snapshot.nodes, ...streamedOnly] });
        activityEngine.setStatus(response.body.session.status); // resolve any still-open rows
        // Session-terminal is authoritative here: settle the flag and close the shared stream. An
        // intermediate execution's terminal event leaves status 'running' (deriveSessionStatus), so the
        // stream stays open for the next execution.
        if (isTerminalStatus(response.body.session.status)) {
          reachedTerminalRef.current = true;
          service?.disconnect();
        }
      } while (pendingReconcileRef.current);
    })();
    inFlightReconcileRef.current = run;
    void run.finally(() => {
      inFlightReconcileRef.current = null;
    });
    return run;
  }, [sessionApiId, engine, activityEngine, handlers, service]);

  // Latest-callback refs so the live-wiring effect can depend ONLY on the stable owned instances — a
  // change to reconcile / handlers must NOT tear down and reconnect the stream.
  const reconcileRef = useRef(reconcile);
  const handlersRef = useRef(handlers);
  useEffect(() => {
    reconcileRef.current = reconcile;
    handlersRef.current = handlers;
  });

  // loadedTerminal only: fetch durable history once, feed the engine. ApiService.call with throw:false
  // RESOLVES an aborted request to { type: Error } (it does NOT reject), so we cannot rely on .catch to
  // ignore StrictMode's abort — gate the apply on `signal.aborted` instead, and capture:false so an
  // expected cancellation is never reported (we report genuine errors ourselves). The AbortController is
  // the single source of truth for "this effect was torn down": it both cancels the in-flight request
  // and (via signal.aborted) blocks a late resolve from applying.
  useEffect(() => {
    if (sourceMode !== 'loadedTerminal') {
      return;
    }
    const controller = new AbortController();
    void ApiService.call({
      endpoint: 'GET /api/v1/ai-agents/sessions/{sessionApiId}/events',
      path: { sessionApiId },
      signal: controller.signal,
      options: { onError: { throw: false, toast: false, capture: false, slug: 'agent-session-events-load' } },
    }).then((response) => {
      if (controller.signal.aborted) {
        return; // stale/aborted — never apply, never error, never report
      }
      if (response.type === ResponseType.Success) {
        // Envelope: { id, redis_stream_id, event }; keep the envelope id for stable row keys.
        activityEngine.ingestAll(response.body.map((entry) => ({ ...entry.event, id: entry.id })));
        setHistoryState('idle');
        return;
      }
      ExceptionHandler.captureException(
        new InVivoError('Failed to load agent activity history', {
          cause: response.error instanceof Error ? response.error : new Error(String(response.error)),
          slug: 'agent-session-events-load',
          level: 'warning',
        })
      );
      setHistoryState('error');
    });
    return () => controller.abort();
  }, [sourceMode, sessionApiId, activityEngine]);

  // Live wiring (beganLive) — subscribe the service to the engine + activity engine + reconciliation in
  // ONE effect, then connect(). One AbortController drives all cleanup. Deps are ONLY the stable owned
  // instances; reconcile/handlers are read via refs, so a callback change never reconnects.
  // Subscribe-before-connect is guaranteed here (synchronous).
  const reconciledForDropRef = useRef(false); // one reconcile per drop episode
  useEffect(() => {
    if (_isNil(service)) {
      return; // loadedTerminal — no live stream
    }
    const controller = new AbortController();
    const { signal } = controller;
    service.subscribe(
      'tree_node_created',
      (event) => {
        const meta = engine.getMeta();
        if (_notNil(meta)) {
          engine.upsertNodes([mapTreeNodeCreated(event, meta, handlersRef.current)]);
        }
      },
      { signal }
    );
    ACTIVITY_EVENT_TYPES.forEach((type) =>
      service.subscribe(type, (event) => activityEngine.ingest(event), { signal })
    );
    // A terminal stream event marks the end of ONE execution, not necessarily the session (one Redis
    // stream is shared across a session's executions, and connect replays from 0-0 — so an earlier
    // execution's terminal event is re-delivered). We therefore CANNOT settle terminal state from the
    // raw event: reconcile /tree and let its authoritative session.status decide (deriveSessionStatus
    // keeps the session 'running' while a follow-up execution is queued/running). The reconcile owns
    // resolving open rows, settling badges, and closing the shared stream once the session is terminal.
    Object.values(TerminalEventStatus).forEach((terminal) =>
      service.subscribe(terminal, () => void reconcileRef.current(), { signal })
    );
    // A dropped connection (status 'reconnecting') while the run may still be live → reconcile via
    // /tree once, in case the run finished during the gap. Fires once per drop, re-armed on re-open.
    service.subscribeStatus(
      () => {
        const status = service.getConnectionStatus();
        if (status === 'reconnecting' && !reconciledForDropRef.current) {
          reconciledForDropRef.current = true;
          void reconcileRef.current();
        } else if (status === 'open') {
          reconciledForDropRef.current = false;
        }
      },
      { signal }
    );
    service.connect(); // AFTER subscribing — no event missed
    return () => {
      controller.abort(); // removes every listener above
      service.disconnect();
    };
  }, [service, engine, activityEngine]);

  return { engine, activityEngine, historyState, reconcile };
};
